/**
 * ネットワーク接続状態の購読。
 *
 * `navigator.onLine` は「復帰したのにイベントが飛ばない」ことがあり（2026-09-13 に
 * Simulator で実際に発生。未送信の回答が同期されないまま残った）、これ単独では
 * オフライン復帰の検知に使えない。ネイティブでは OS の接続状態（iOS の Reachability）を
 * 見る @capacitor/network を正とする。Web ではプラグインの web 実装が
 * navigator.onLine / online イベントへ委譲するため、従来と同じ挙動になる。
 */
import { Network } from "@capacitor/network";

/** 現在の接続状態。取得できない環境では navigator.onLine にフォールバックする */
export async function isNetworkConnected(): Promise<boolean> {
  try {
    const status = await Network.getStatus();
    return status.connected;
  } catch {
    return navigator.onLine;
  }
}

/**
 * 接続状態の変化を購読する。戻り値を呼ぶと解除する。
 * 圏外の間はイベントが来ないため、再試行のポーリングは不要。
 */
export function subscribeNetworkStatus(
  onChange: (connected: boolean) => void,
): () => void {
  let removeListener: (() => void) | undefined;
  let unsubscribed = false;

  Network.addListener("networkStatusChange", ({ connected }) => onChange(connected))
    .then((handle) => {
      if (unsubscribed) {
        handle.remove();
      } else {
        removeListener = () => handle.remove();
      }
    })
    .catch(() => {
      // プラグイン未対応環境では window の online/offline 側だけで動かす
    });

  return () => {
    unsubscribed = true;
    removeListener?.();
  };
}
